import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router'
import { Form, Flex, Button } from 'antd'
import { ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION } from '../../../../constants/response-messages'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect } from 'react-redux'
import Routes from '../../../../constants/routes'
import { AssignmentSections } from '../../../../constants/assignment-sections'
import useProjectionCostEstimation from '../../../../hooks/useProjectionCostEstimation'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { VERSION_ID_PARAM } from '../../../../constants/query-params'
import { useNavigate } from 'react-router-dom'
import './discovery-descriptions-review.scss'
import _ from 'lodash'
import { LLM_VERSIONS } from '../../../../constants/llm-versions'
import {
    useDeleteConceptualLabelDiscoveryMutation,
    useUpdateConceptualLabelDiscoveryMutation,
} from '../../../../store/apis/conceptual-label.api'
import {
    useUpdateDiscoveryComponentMutation,
    useDeleteDiscoveryComponentMutation,
} from '../../../../store/apis/discovery-component.api'
import { usePeriodicSaving } from './hooks/usePeriodicSaving'
import usePageTransition from '../../../../hooks/usePageTransition'
import ComponentsTabs from './components/ComponentsTabs'
import CreatePatternsButton from './components/CreatePatternsButton'
import FilterPatterns from './components/FilterPatterns'

/**
 * Review section for discovery descriptions
 *
 * This component displays patterns discovered from either:
 * - Semantic discovery (ML-based pattern detection)
 * - Conceptual labels discovery (based on discipline selection)
 *
 * The component uses deferred rendering to improve initial load time.
 */
const DiscoveryDescriptionsReviewSection = ({
    descriptionsSuggestedBySemanticDiscovery,
    setDescriptionsSuggestedBySemanticDiscovery,
    createSemanticPatterns,
    projectCriteria,
}) => {
    const { token, layerId } = useParams()
    const versionId = useVersionId()
    const [deleteConceptualLabelDiscovery] = useDeleteConceptualLabelDiscoveryMutation()
    const [updateConceptualLabelDiscovery] = useUpdateConceptualLabelDiscoveryMutation()

    // Discovery component mutations
    const [updateDiscoveryComponent] = useUpdateDiscoveryComponentMutation()
    const [deleteDiscoveryComponent] = useDeleteDiscoveryComponentMutation()

    // UI state
    const showPlaceholders = usePageTransition()
    const [isFormSubmitting, setIsFormSubmitting] = useState(false)
    const [criteriaUpdating, setCriteriaUpdating] = useState([])
    const [selectedPatternIds, setSelectedPatternIds] = useState([])
    const [activeComponentId, setActiveComponentId] = useState(null)
    const [activeFilters, setActiveFilters] = useState({
        warningLevels: null,
        hasConceptualLabel: null,
    })

    const [form] = Form.useForm()
    const navigate = useNavigate()

    // Helper function to format a pattern's data from form values
    const getPatternDataFromForm = useCallback(
        patternId => {
            // Find the pattern in the descriptionsSuggestedBySemanticDiscovery patterns array
            const semanticPattern = descriptionsSuggestedBySemanticDiscovery?.patterns?.find(
                pattern => pattern.id === patternId,
            )

            if (!semanticPattern) return null

            // Get form values for this pattern

            const name = form.getFieldValue(`name-${patternId}`)
            const hit_feedback = form.getFieldValue(`hit-feedback-${patternId}`)
            const miss_feedback = form.getFieldValue(`miss-feedback-${patternId}`)
            const show_hit_reasoning = form.getFieldValue(`show-hit-reasoning-${patternId}`)
            const show_miss_reasoning = form.getFieldValue(`show-miss-reasoning-${patternId}`)
            const description = form.getFieldValue(`description-${patternId}`)
            const color_id = form.getFieldValue(`color-${patternId}`)
            const warning_level = form.getFieldValue(`warning-level-${patternId}`)
            const grade = form.getFieldValue(`grade-${patternId}`)
            const grouping_cap = form.getFieldValue(`grouping-cap-${patternId}`)

            const clFormValue = form.getFieldValue(`cl-${patternId}`)
            const conceptual_labels =
                clFormValue !== undefined ? clFormValue : semanticPattern.proposed_conceptual_labels || []

            const payload = {
                patternId,
            }

            // Only include fields that have been explicitly set or have values
            if (name !== undefined) payload.name = name
            if (hit_feedback !== undefined) payload.feedback = hit_feedback
            if (miss_feedback !== undefined) payload.miss_feedback = miss_feedback
            if (show_hit_reasoning !== undefined) payload.show_hit_reasoning = show_hit_reasoning
            if (show_miss_reasoning !== undefined) payload.show_miss_reasoning = show_miss_reasoning
            if (description !== undefined) payload.description = description
            if (color_id !== undefined) payload.color_id = color_id
            if (warning_level !== undefined) payload.warning_level = warning_level
            if (grade !== undefined) payload.grade = grade
            if (grouping_cap !== undefined) payload.grouping_cap = grouping_cap

            // Always include criteria as it's required
            payload.criteria = semanticPattern.criteria.map(criterion => criterion.value)

            if (conceptual_labels !== undefined) {
                payload.proposed_conceptual_labels = conceptual_labels
            }

            return payload
        },
        [descriptionsSuggestedBySemanticDiscovery, form],
    )

    // Use the periodic saving hook
    const { isSaving, saveAllChangedPatterns } = usePeriodicSaving(
        form,
        descriptionsSuggestedBySemanticDiscovery,
        15000,
        getPatternDataFromForm,
    )

    // Handler for pattern selection changes - use useCallback to prevent recreation
    const handlePatternSelectChange = useCallback((patternId, isSelected) => {
        setSelectedPatternIds(prevSelected => {
            if (isSelected) {
                return [...prevSelected, patternId]
            } else {
                return prevSelected.filter(id => id !== patternId)
            }
        })
    }, [])

    // Handle filter changes
    const handleFilterChange = useCallback(newFilters => {
        setActiveFilters(newFilters)
    }, [])

    // Handle select all/deselect all patterns
    const handleSelectAllPatterns = useCallback(() => {
        if (!descriptionsSuggestedBySemanticDiscovery?.patterns?.length) return

        const allPatternIds = descriptionsSuggestedBySemanticDiscovery.patterns.map(pattern => pattern.id)

        const allSelected = allPatternIds.length > 0 && allPatternIds.every(id => selectedPatternIds.includes(id))

        if (allSelected) {
            // Deselect all
            setSelectedPatternIds([])
        } else {
            // Select all
            setSelectedPatternIds(allPatternIds)
        }
    }, [descriptionsSuggestedBySemanticDiscovery, selectedPatternIds])

    // Calculate projection cost only for selected patterns
    const criteriaListsForSelectedPatterns = useMemo(() => {
        if (selectedPatternIds.length === 0) {
            return []
        }

        return descriptionsSuggestedBySemanticDiscovery?.patterns
            ?.filter(pattern => selectedPatternIds.includes(pattern.id))
            .map(pattern => pattern.criteria.map(criterion => criterion.value))
    }, [descriptionsSuggestedBySemanticDiscovery, selectedPatternIds])

    const projectionCostDetails = useProjectionCostEstimation(criteriaListsForSelectedPatterns)

    const isAssignmentTooBigForProjection = projectionCostDetails === ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION
    const isEstimatingCost = projectionCostDetails === '...'

    // Redirect to home page if no patterns to display
    useEffect(() => {
        if (!descriptionsSuggestedBySemanticDiscovery?.patterns?.length) {
            navigateToHomePage()
        }
    }, [descriptionsSuggestedBySemanticDiscovery, navigateToHomePage])

    /**
     * Navigate back to the assignment home page
     */
    const navigateToHomePage = useCallback(() => {
        navigate({
            pathname: Routes.ASSIGNMENT_FULL.replace(':token', token)
                .replace(':section', AssignmentSections.HOME)
                .replace(':layerId', layerId),
            search: versionId ? `?${VERSION_ID_PARAM}=${versionId}` : '',
        })
    }, [navigate, token, layerId, versionId])

    /**
     * Submit the form to create semantic patterns
     */
    const handleCreatePatterns = useCallback(async () => {
        setIsFormSubmitting(true)
        try {
            await saveAllChangedPatterns()

            // Only format and save selected patterns
            const patternsToSave =
                selectedPatternIds.length > 0
                    ? descriptionsSuggestedBySemanticDiscovery.patterns.filter(pattern =>
                          selectedPatternIds.includes(pattern.id),
                      )
                    : descriptionsSuggestedBySemanticDiscovery.patterns

            const patternIds = patternsToSave.map(({ id }) => id)

            const success = await createSemanticPatterns(token, layerId, patternIds)

            if (success) {
                navigateToHomePage()
            } else {
                setIsFormSubmitting(false)
            }
        } catch (error) {
            console.error('Error creating patterns:', error)
            setIsFormSubmitting(false)
        }
    }, [
        selectedPatternIds,
        descriptionsSuggestedBySemanticDiscovery,
        saveAllChangedPatterns,
        createSemanticPatterns,
        token,
        layerId,
        navigateToHomePage,
    ])

    /**
     * Update criteria fields for a specific pattern
     */
    const updateCriteriaFields = useCallback(
        (patternId, updatedFields) => {
            const updatedData = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)
            const targetPattern = updatedData.patterns?.find(pattern => pattern.id === patternId)

            if (targetPattern) {
                targetPattern.criteria = updatedFields
                setDescriptionsSuggestedBySemanticDiscovery(updatedData)
            }
        },
        [descriptionsSuggestedBySemanticDiscovery, setDescriptionsSuggestedBySemanticDiscovery],
    )

    /**
     * Remove a pattern suggestion
     */
    const handleRemovePattern = useCallback(
        async patternId => {
            try {
                await deleteConceptualLabelDiscovery({ patternId })

                setSelectedPatternIds(prevSelected => prevSelected.filter(id => id !== patternId))
            } catch (error) {
                console.error('Error triggering pattern removal:', error)
            }
        },
        [deleteConceptualLabelDiscovery],
    )

    /**
     * Regenerate and project criteria for a specific pattern
     */
    const handleRegenerateAndProjectCriteria = useCallback(
        async patternId => {
            setCriteriaUpdating(prev => [...prev, patternId])

            try {
                const pattern = descriptionsSuggestedBySemanticDiscovery.patterns?.find(p => p.id === patternId)
                const criteriaValues = pattern.criteria.map(criterion => criterion.value)

                const projection = await projectCriteria(
                    token,
                    criteriaValues,
                    LLM_VERSIONS.DEEP.value,
                    pattern.file_id,
                )

                if (projection) {
                    // Format the updated criteria with projection results
                    const updatedCriteria = projection.map((p, index) => ({
                        id: String(index + 1),
                        value: p.criterion,
                        isMet: p.is_met,
                        reasoning: p.reasoning,
                        matchConfidenceInPercent:
                            typeof p.match_confidence === 'number' ? p.match_confidence * 100 : null,
                    }))

                    // Update criteria fields
                    updateCriteriaFields(patternId, updatedCriteria)

                    // Update projection in the semantic pattern
                    const updatedData = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)
                    const patternToUpdate = updatedData.patterns?.find(p => p.id === patternId)

                    if (patternToUpdate) {
                        patternToUpdate.projection = projection
                        setDescriptionsSuggestedBySemanticDiscovery(updatedData)
                    }
                }
            } finally {
                setCriteriaUpdating(prev => prev.filter(id => id !== patternId))
            }
        },
        [
            descriptionsSuggestedBySemanticDiscovery,
            projectCriteria,
            token,
            updateCriteriaFields,
            setDescriptionsSuggestedBySemanticDiscovery,
        ],
    )

    const handleContinueLater = useCallback(async () => {
        await saveAllChangedPatterns()
        navigateToHomePage()
    }, [saveAllChangedPatterns, navigateToHomePage])

    const handleRenameComponent = useCallback(
        async (componentId, newName) => {
            try {
                const component = descriptionsSuggestedBySemanticDiscovery?.components?.find(c => c.id === componentId)
                if (component) {
                    await updateDiscoveryComponent({
                        componentId,
                        name: newName,
                        order: component.order,
                    })
                }
            } catch (error) {
                console.error('Error renaming component:', error)
            }
        },
        [updateDiscoveryComponent, descriptionsSuggestedBySemanticDiscovery],
    )

    const handleDeleteComponent = useCallback(
        async componentId => {
            try {
                await deleteDiscoveryComponent({ componentId })
            } catch (error) {
                console.error('Error deleting component:', error)
            }
        },
        [deleteDiscoveryComponent],
    )

    // Handle moving patterns between components
    const handlePatternMove = useCallback(
        async (patternId, targetComponentId) => {
            try {
                // Update local state optimistically
                const updatedData = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)
                const pattern = updatedData.patterns.find(p => p.id === patternId)

                if (pattern) {
                    const oldComponentId = pattern.discovery_component_id

                    // Update pattern's component assignment
                    pattern.discovery_component_id = targetComponentId

                    // Update local state immediately for better UX
                    setDescriptionsSuggestedBySemanticDiscovery(updatedData)

                    // Make API call to persist the change
                    await updateConceptualLabelDiscovery({
                        patternId,
                        discovery_component_id: targetComponentId,
                    })

                    console.log(`Moved pattern ${patternId} from component ${oldComponentId} to ${targetComponentId}`)
                }
            } catch (error) {
                console.error('Error moving pattern:', error)
                // TODO: Revert optimistic update on error
            }
        },
        [
            descriptionsSuggestedBySemanticDiscovery,
            setDescriptionsSuggestedBySemanticDiscovery,
            updateConceptualLabelDiscovery,
        ],
    )

    // Handle moving multiple selected patterns to a component
    const handleMoveSelectedPatterns = useCallback(
        async targetComponentId => {
            if (selectedPatternIds.length === 0) return

            try {
                // Update local state optimistically
                const updatedData = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)

                // Update all selected patterns
                const promises = selectedPatternIds.map(async patternId => {
                    const pattern = updatedData.patterns.find(p => p.id === patternId)
                    if (pattern) {
                        pattern.discovery_component_id = targetComponentId

                        // Make API call for each pattern
                        return updateConceptualLabelDiscovery({
                            patternId,
                            discovery_component_id: targetComponentId,
                        })
                    }
                })

                // Update local state immediately
                setDescriptionsSuggestedBySemanticDiscovery(updatedData)

                // Wait for all API calls to complete
                await Promise.all(promises)

                console.log(`Moved ${selectedPatternIds.length} patterns to component ${targetComponentId}`)
            } catch (error) {
                console.error('Error moving selected patterns:', error)
                // TODO: Revert optimistic update on error
            }
        },
        [
            selectedPatternIds,
            descriptionsSuggestedBySemanticDiscovery,
            setDescriptionsSuggestedBySemanticDiscovery,
            updateConceptualLabelDiscovery,
        ],
    )

    // Handle successful delete operation by clearing selection
    const handleDeleteSuccess = useCallback(() => {
        setSelectedPatternIds([])
    }, [])

    // Create handlers
    const handleCreateSelectedPatterns = useCallback(async () => {
        if (!descriptionsSuggestedBySemanticDiscovery?.patterns?.length || selectedPatternIds.length === 0) return
        setIsFormSubmitting(true)
        try {
            await saveAllChangedPatterns()
            const patternIds = selectedPatternIds
            const success = await createSemanticPatterns(token, layerId, patternIds)
            if (success) navigateToHomePage()
            else setIsFormSubmitting(false)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error creating selected patterns:', e)
            setIsFormSubmitting(false)
        }
    }, [
        descriptionsSuggestedBySemanticDiscovery,
        selectedPatternIds,
        saveAllChangedPatterns,
        createSemanticPatterns,
        token,
        layerId,
        navigateToHomePage,
    ])

    const handleCreateAllPatterns = useCallback(async () => {
        if (!descriptionsSuggestedBySemanticDiscovery?.patterns?.length) return
        setIsFormSubmitting(true)
        try {
            await saveAllChangedPatterns()
            const patternIds = descriptionsSuggestedBySemanticDiscovery.patterns.map(p => p.id)
            const success = await createSemanticPatterns(token, layerId, patternIds)
            if (success) navigateToHomePage()
            else setIsFormSubmitting(false)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error creating all patterns:', e)
            setIsFormSubmitting(false)
        }
    }, [
        descriptionsSuggestedBySemanticDiscovery,
        saveAllChangedPatterns,
        createSemanticPatterns,
        token,
        layerId,
        navigateToHomePage,
    ])

    const handleCreateCurrentComponentPatterns = useCallback(async () => {
        if (!descriptionsSuggestedBySemanticDiscovery?.patterns?.length || !activeComponentId) return
        setIsFormSubmitting(true)
        try {
            await saveAllChangedPatterns()
            const patternIds = descriptionsSuggestedBySemanticDiscovery.patterns
                .filter(p => String(p.discovery_component_id) === String(activeComponentId))
                .map(p => p.id)
            if (patternIds.length === 0) {
                setIsFormSubmitting(false)
                return
            }
            const success = await createSemanticPatterns(token, layerId, patternIds)
            if (success) navigateToHomePage()
            else setIsFormSubmitting(false)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error creating component patterns:', e)
            setIsFormSubmitting(false)
        }
    }, [
        descriptionsSuggestedBySemanticDiscovery,
        activeComponentId,
        saveAllChangedPatterns,
        createSemanticPatterns,
        token,
        layerId,
        navigateToHomePage,
    ])

    return (
        <Form
            name='descriptions-review'
            autoComplete='off'
            form={form}
            onFinish={handleCreatePatterns}
            requiredMark={false}
            layout='vertical'
        >
            <Flex justify='space-between' className='mb-8' gap={8} wrap='wrap'>
                <Flex gap={8} wrap='wrap'>
                    <Button onClick={handleSelectAllPatterns}>
                        {descriptionsSuggestedBySemanticDiscovery?.patterns?.length > 0 &&
                        descriptionsSuggestedBySemanticDiscovery.patterns.every(pattern =>
                            selectedPatternIds.includes(pattern.id),
                        )
                            ? 'Deselect All'
                            : 'Select All'}
                    </Button>
                    <FilterPatterns onApplyFilters={handleFilterChange} />
                </Flex>
                <CreatePatternsButton
                    selectedPatternIds={selectedPatternIds}
                    projectionCostDetails={projectionCostDetails}
                    isFormSubmitting={isFormSubmitting}
                    isEstimatingCost={isEstimatingCost}
                    isSaving={isSaving}
                    isAssignmentTooBigForProjection={isAssignmentTooBigForProjection}
                    handleContinueLater={handleContinueLater}
                    onMoveSelectedPatterns={handleMoveSelectedPatterns}
                    availableComponents={descriptionsSuggestedBySemanticDiscovery?.components || []}
                    onDeleteSuccess={handleDeleteSuccess}
                    onCreateSelectedPatterns={handleCreateSelectedPatterns}
                    onCreateAllPatterns={handleCreateAllPatterns}
                    onCreateCurrentComponentPatterns={handleCreateCurrentComponentPatterns}
                    activeComponentId={activeComponentId}
                    currentComponentPatternsCount={
                        (descriptionsSuggestedBySemanticDiscovery?.patterns || []).filter(
                            p => String(p.discovery_component_id) === String(activeComponentId),
                        ).length
                    }
                />
            </Flex>
            <ComponentsTabs
                components={descriptionsSuggestedBySemanticDiscovery?.components || []}
                patterns={descriptionsSuggestedBySemanticDiscovery?.patterns || []}
                form={form}
                showPlaceholders={showPlaceholders}
                criteriaUpdating={criteriaUpdating}
                updateCriteriaFields={updateCriteriaFields}
                onRegenerateAndProject={handleRegenerateAndProjectCriteria}
                selectedPatternIds={selectedPatternIds}
                onPatternSelectChange={handlePatternSelectChange}
                filters={activeFilters}
                onRemove={handleRemovePattern}
                onRenameComponent={handleRenameComponent}
                onDeleteComponent={handleDeleteComponent}
                onPatternMove={handlePatternMove}
                onActiveChange={setActiveComponentId}
            />
        </Form>
    )
}

const mapState = ({ assignment }) => ({
    descriptionsSuggestedBySemanticDiscovery: assignment.descriptionsSuggestedBySemanticDiscovery,
})

const mapDispatch = dispatch => ({
    createSemanticPatterns: (token, layerId, patternIds) =>
        dispatch(actions.createSemanticPatterns(token, layerId, patternIds)),
    setDescriptionsSuggestedBySemanticDiscovery: value =>
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(value)),
    projectCriteria: (token, criteria, llmVersion, submissionId) =>
        dispatch(actions.projectCriteria(token, criteria, false, llmVersion, submissionId)),
})

export default connect(mapState, mapDispatch)(DiscoveryDescriptionsReviewSection)
